// @ts-check
// The single HTTP client. Every service module goes through this and nothing
// else calls axios directly.
//
// WHAT WAS DELIBERATELY LEFT OUT, because the copied client had all of it and
// each one is actively wrong against this API:
//
//   NO RESPONSE CACHE. The old client cached every GET for an hour in
//   IndexedDB and invalidated by URL pattern. Against an API where a cohort
//   fills, an assessment is confirmed and a credential is issued by *other
//   people*, a stale read is not a performance win — it is how a client offers
//   a seat that is already taken. Caching belongs in a query layer with
//   explicit invalidation, not in a transport interceptor.
//
//   NO 401 REFRESH-AND-RETRY LOOP. Renewal happens on the SERVER, which
//   refreshes the access token held in the session before it validates the
//   request (PLATFORM-PLAN-9). A 401 here means the session itself is gone —
//   signed out elsewhere, or the realm session ended — and retrying cannot
//   bring it back.
//
//   NO TOKEN AND NO withCredentials. The browser holds an HttpOnly session
//   cookie and no token at all; same-origin requests carry the cookie without
//   withCredentials. What every request DOES carry is X-CSRF: 1, without which
//   the server will not accept the cookie as a credential
//   (Academy.Server/Auth/BffCsrf.cs). Anonymous reads — the landing page, the
//   verifier, the checkout — carry it too and are unaffected: with no session
//   there is simply nothing for the server to convert.

import axios from 'axios';
import { attachCurrencyHeader } from '@binaa-almajal/ui-kit/utils/currencyHeader';
import { apiBaseUrl } from '../config/environment';
import { toAcademyError, AcademyApiError, ClientErrorCodes } from './errors';
import { CSRF_HEADER } from '../auth/bffTokenSource';

/**
 * @typedef {import('@academy-api').PageRequest} PageRequest
 * @typedef {import('@academy-api').BatchMode} BatchMode
 */

const client = axios.create({
    baseURL: apiBaseUrl,
    timeout: 30_000,
    withCredentials: false,
    headers: { Accept: 'application/json', [CSRF_HEADER]: '1' },
});

// THE REQUEST CURRENCY (X-Currency), read at send time so a switch in the
// header applies to the very next call. The server stamps new price-book rows
// with it and refuses a body that states another; it never converts anything.
attachCurrencyHeader(client);

// WHICH ORGANISATION THE COMPANY PORTAL IS ABOUT (A4, R11). A person may manage
// several companies or universities; the server then needs `organisationId` on
// every /company/* call and refuses an organisation the caller does not manage.
// Set once by the portal's organisation picker and added HERE, to every
// /company/* request, so no service has to remember to pass it. With one managed
// organisation nothing is set and the server uses that one.
/** @type {string|null} */
let companyOrganisationId = null;

/** @param {string|null|undefined} id */
export function setCompanyOrganisation(id) {
    companyOrganisationId = id || null;
}

client.interceptors.request.use((config) => {
    if (companyOrganisationId
        && typeof config.url === 'string'
        && config.url.startsWith('/company/')) {
        config.params = { ...config.params, organisationId: companyOrganisationId };
    }
    return config;
});

/** Fired once per actor_unmatched failure; the shell listens and takes over. */
export const ACTOR_UNMATCHED_EVENT = 'academy:actor-unmatched';

client.interceptors.response.use(
    (response) => response,
    (error) => {
        const academyError = toAcademyError(error);
        // B9: actor_unmatched is a whole-session condition, not a per-screen
        // one — every request this identity makes will answer the same way,
        // and a generic "no permission" sends the person into a sign-out/
        // sign-in loop that can never work. Announced here, once, at the only
        // layer that sees every response; the shell renders the explanation.
        if (academyError.needsProvisioning) {
            window.dispatchEvent(
                new CustomEvent(ACTOR_UNMATCHED_EVENT, { detail: academyError }));
        }
        return Promise.reject(academyError);
    },
);

/**
 * Serialises a PageRequest. Cursor pagination: `nextCursor` from the previous
 * PagedResult goes back in as `cursor`. `totalCount` is never populated by any
 * endpoint, so do not build a numbered pager on it.
 * @param {PageRequest} [page]
 */
function pageParams(page) {
    if (!page) return {};
    const { cursor, size, sortBy, descending } = page;
    return {
        ...(cursor != null && { cursor }),
        ...(size != null && { size }),
        ...(sortBy != null && { sortBy }),
        ...(descending != null && { descending }),
    };
}

/**
 * @param {string} url
 * @param {{ params?: Record<string, unknown>, page?: PageRequest, signal?: AbortSignal }} [options]
 */
export async function get(url, options = {}) {
    const { data } = await client.get(url, {
        params: { ...options.params, ...pageParams(options.page) },
        signal: options.signal,
    });
    return data;
}

/**
 * POST. `idempotencyKey` is optional and must be generated ONCE PER USER
 * ACTION, at the click — not per attempt. See newIdempotencyKey.
 *
 * A replay returns the ORIGINAL response: the same status, the same body and
 * the same Location. It is not a fresh 201 and not a 409, so a caller cannot
 * tell a replay from the first success, and does not need to.
 *
 * `headers` exists for exactly one caller today: POST .../retire binds a BARE
 * DateOnly, so the body is a JSON scalar and axios will not mark a plain string
 * as application/json on its own. Do not reach for it to smuggle auth or
 * caching headers — those belong to the client's defaults and interceptors.
 *
 * @param {string} url
 * @param {unknown} [body]
 * @param {{ idempotencyKey?: string, params?: Record<string, unknown>, headers?: Record<string, string>, signal?: AbortSignal }} [options]
 */
export async function post(url, body, options = {}) {
    const response = await client.post(url, body, {
        params: options.params,
        signal: options.signal,
        headers: {
            ...(options.idempotencyKey && { 'Idempotency-Key': options.idempotencyKey }),
            ...options.headers,
        },
    });
    return response.data;
}

/**
 * PATCH. THE BODY MUST CONTAIN ONLY THE FIELDS THE USER CHANGED.
 *
 * Every PATCH body on this API uses tri-state Opt<T> fields: an absent key
 * leaves the column alone, an explicit null WRITES NULL. Serialising a whole
 * form object therefore erases every field the user did not fill in, and it
 * does so silently with a 200. `JSON.stringify(form)` is never a correct patch
 * body — build one with buildPatch() from ./patch.
 *
 * @param {string} url
 * @param {Record<string, unknown>} body
 * @param {{ params?: Record<string, unknown>, signal?: AbortSignal }} [options]
 */
export async function patch(url, body, options = {}) {
    const response = await client.patch(url, body, {
        params: options.params,
        signal: options.signal,
    });
    return response.data;
}

/**
 * @param {string} url
 * @param {unknown} [body]
 * @param {{ params?: Record<string, unknown>, signal?: AbortSignal }} [options]
 */
export async function put(url, body, options = {}) {
    const response = await client.put(url, body, {
        params: options.params,
        signal: options.signal,
    });
    return response.data;
}

/**
 * DELETE. `del` because `delete` is a reserved word. Rare on this API by
 * design — most removals are state transitions (revoke, deactivate, withdraw)
 * so the record and its audit trail survive. Success is a 204 with no body;
 * a refusal (a frozen curriculum version, a row something still references)
 * arrives as a thrown AcademyApiError like every other failure.
 * @param {string} url
 * @param {{ params?: Record<string, unknown>, signal?: AbortSignal }} [options]
 */
export async function del(url, options = {}) {
    const response = await client.delete(url, {
        params: options.params,
        signal: options.signal,
    });
    return response.data;
}

/**
 * A batch call. `mode` is REQUIRED by the server — there is no default and
 * omitting it is a 400.
 *
 * A 207 IS NOT A SUCCESS. In bestEffort mode the request "succeeded" while
 * individual items failed, so this returns the BatchResult and the caller must
 * check `allSucceeded` before showing anything green. In atomic mode a failure
 * is a single ProblemDetails and nothing was written at all — that arrives here
 * as a thrown AcademyApiError, not as a result with failures.
 *
 * @template TKey
 * @param {string} url
 * @param {unknown} body
 * @param {BatchMode} mode
 * @param {{ idempotencyKey?: string, params?: Record<string, unknown>, inQuery?: boolean, signal?: AbortSignal }} [options]
 * @returns {Promise<import('@academy-api').BatchResult<TKey>>}
 */
export async function postBatch(url, body, mode, options = {}) {
    // The batch PATCH endpoints take `mode` as a query parameter; the batch
    // POST endpoints take it in the body. Getting this backwards is a 400 that
    // reads like a validation problem with the payload.
    return options.inQuery
        ? post(url, body, { ...options, params: { ...options.params, mode } })
        : post(url, { ...(/** @type {object} */ (body)), mode }, options);
}

/**
 * True when a batch response needs the failure path even though the request
 * itself succeeded.
 * @param {import('@academy-api').BatchResult<unknown>} result
 */
export function batchFailed(result) {
    return !result?.allSucceeded;
}

export { client as httpClient, AcademyApiError, ClientErrorCodes };
